import MaterialArticleHistory from './MaterialArticleHistory';
import { useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  TableBodySkeleton,
} from '../../../common/components';
import { useInventoryArticle } from '../../../hooks/useInventoryArticles';
import { useArticleStocksByArticle } from '../../../hooks/useArticleStocks';
import { useStockMovements } from '../../../hooks/useStockMovements';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { useUnits } from '../../../hooks/useUnits';
import { ALL_WAREHOUSES_FILTER } from '../constants/inventoryArticlesConstants';
import {
  getArticleLabel,
  getUnitDescription,
  getWarehouseLabel,
} from '../utils/inventoryArticlesUtils';
import { useWarehousesByUuids } from '../hooks/useWarehousesByUuids';
import ArticleStockByWarehouseTable from './ArticleStockByWarehouseTable';
import ArticleStockMovementsTable from './ArticleStockMovementsTable';
import AdjustArticleStockDialog from './AdjustArticleStockDialog';
import { usePermissions } from '../../../hooks/usePermissions';
import { guardDialogClose, isAnyActionPending } from '../../utils/actionLockUtils';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';

/**
 * Detalle de un artículo con existencias, movimientos y, si corresponde, su historia de costos.
 * @param {Object} props
 * @param {boolean} props.open Visibilidad.
 * @param {Function} props.onClose Cierra el diálogo.
 * @param {string} props.articleId UUID del artículo.
 * @param {Function} props.onPendingChange Informa si hay una acción en curso.
 * @param {boolean} [props.showCostHistory] Muestra la historia de costos (Tablero de materiales).
 * @param {Object} [props.costHistoryLayers] Capas FIFO paginadas y ordenables.
 * @param {Object} [props.costHistoryPurchases] Compras paginadas y ordenables.
 * @returns {JSX.Element}
 */
const InventoryArticleDetailDialog = ({
  open,
  onClose,
  articleId,
  onPendingChange,
  showCostHistory = false,
  costHistoryLayers,
  costHistoryPurchases,
}) => {
  const { permissions } = usePermissions();
  const canModifyStock = permissions.canModifyStock;
  const [selectedStockRow, setSelectedStockRow] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [nestedActionPending, setNestedActionPending] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState(ALL_WAREHOUSES_FILTER);

  const { data: units = [] } = useUnits({ enabled: open && Boolean(articleId) });
  const { data: warehouses = [] } = useWarehouses({
    enabled: open && Boolean(articleId),
  });

  const { data: article, isLoading: articleLoading } = useInventoryArticle(articleId, {
    enabled: open && Boolean(articleId),
  });

  const {
    data: stocks = [],
    isLoading: stocksLoading,
    refetch: refetchStocks,
  } = useArticleStocksByArticle(articleId, {
    enabled: open && Boolean(articleId),
  });

  const movementFilters = useMemo(() => {
    const filters = { uuid_inventory_article: articleId };

    if (warehouseFilter !== ALL_WAREHOUSES_FILTER) {
      filters.uuid_warehouse = warehouseFilter;
    }

    return filters;
  }, [articleId, warehouseFilter]);

  const { data: movements = [], isLoading: movementsLoading } = useStockMovements(
    movementFilters,
    { enabled: open && Boolean(articleId) }
  );

  const warehouseUuids = useMemo(
    () => [
      ...new Set([
        ...stocks.map((stock) => stock.uuid_warehouse),
        ...movements.map((movement) => movement.uuid_warehouse),
      ]),
    ],
    [stocks, movements]
  );

  const { warehousesByUuid, isLoading: warehousesLoading } = useWarehousesByUuids(
    warehouseUuids,
    open && Boolean(articleId)
  );

  const warehousesLookup = useMemo(() => {
    if (Object.keys(warehousesByUuid).length > 0) {
      return warehousesByUuid;
    }

    return warehouses.reduce((acc, warehouse) => {
      if (warehouse?.uuid) {
        acc[warehouse.uuid] = warehouse;
      }
      return acc;
    }, {});
  }, [warehousesByUuid, warehouses]);

  const warehouseOptions = useMemo(
    () =>
      toWarehouseOptions(
        warehouseUuids.map(
          (uuid) =>
            warehousesLookup[uuid] || {
              uuid,
              name: getWarehouseLabel(warehousesLookup, uuid),
            }
        )
      ),
    [warehouseUuids, warehousesLookup]
  );

  const sortedMovements = useMemo(
    () =>
      [...movements].sort(
        (left, right) => new Date(right.created_at) - new Date(left.created_at)
      ),
    [movements]
  );

  const handleOpenAdjustDialog = (stockRow = null) => {
    setSelectedStockRow(stockRow);
    setAdjustDialogOpen(true);
  };

  const handleCloseAdjustDialog = () => {
    setAdjustDialogOpen(false);
    setSelectedStockRow(null);
  };

  const handleAdjustSuccess = async () => {
    await refetchStocks();
  };

  const isActionLocked = isAnyActionPending(nestedActionPending);

  const handleClose = () => {
    if (isActionLocked) return;
    setWarehouseFilter(ALL_WAREHOUSES_FILTER);
    setSelectedStockRow(null);
    setAdjustDialogOpen(false);
    onPendingChange?.(false);
    onClose();
  };

  const isLoading = articleLoading;
  const stockSectionLoading = stocksLoading || warehousesLoading;

  if (!open) return null;

  if (isLoading || !article) {
    return (
      <DialogContainer
        open={open}
        onClose={guardDialogClose(isActionLocked, handleClose)}
        size="large"
        minHeight="60vh"
      >
        <DialogTitle>Detalle del artículo</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={handleClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelButtonProps={{ disabled: isActionLocked }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <>
      <DialogContainer
        open={open}
        onClose={guardDialogClose(isActionLocked, handleClose)}
        size="large"
        minHeight="70vh"
      >
        <DialogTitle>{getArticleLabel(article)}</DialogTitle>

        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Código: ${article.code}`} variant="outlined" />
              <Chip
                label={`Unidad: ${getUnitDescription(units, article.uuid_unit)}`}
                variant="outlined"
              />
              <Chip
                label={article.is_active ? 'Activo' : 'Inactivo'}
                color={article.is_active ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>

            {article.description && (
              <Typography variant="body2" color="text.secondary">
                {article.description}
              </Typography>
            )}

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Stock por depósito
                </Typography>
                {canModifyStock && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    onClick={() => handleOpenAdjustDialog(null)}
                    disabled={isActionLocked}
                  >
                    Ajustar stock
                  </Button>
                )}
              </Box>
              <ArticleStockByWarehouseTable
                stocks={stocks}
                warehousesByUuid={warehousesLookup}
                isLoading={stockSectionLoading}
                canModifyStock={canModifyStock}
                actionsDisabled={isActionLocked}
                onAdjustStock={canModifyStock ? handleOpenAdjustDialog : undefined}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Historial de movimientos
              </Typography>
              <ArticleStockMovementsTable
                movements={sortedMovements}
                warehousesByUuid={warehousesLookup}
                warehouseFilter={warehouseFilter}
                warehouseOptions={warehouseOptions}
                onWarehouseFilterChange={setWarehouseFilter}
                isLoading={movementsLoading || warehousesLoading}
              />
            </Box>

            {showCostHistory && (
              <MaterialArticleHistory layers={costHistoryLayers} purchases={costHistoryPurchases} />
            )}
          </Box>
        </DialogBody>

        <DialogActions
          handleClose={handleClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelButtonProps={{ disabled: isActionLocked }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>

      {canModifyStock && (
        <AdjustArticleStockDialog
          open={adjustDialogOpen}
          onClose={handleCloseAdjustDialog}
          article={article}
          stockRow={selectedStockRow}
          stocks={stocks}
          warehouses={warehouses}
          warehousesByUuid={warehousesLookup}
          onSuccess={handleAdjustSuccess}
          onPendingChange={(isPending) => {
            setNestedActionPending(isPending);
            onPendingChange?.(isPending);
          }}
        />
      )}
    </>
  );
};

export default InventoryArticleDetailDialog;
